import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { attendanceAPI, leaveAPI } from "../services/api";
import { getGreeting, isGeofenceActive } from "../utils/helpers";
import { PageLoader } from "../components/common/LoadingSpinner";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastLoad = useRef(0);

  const load = useCallback(async () => {
    const now = Date.now();
    if (now - lastLoad.current < 3000) return;
    lastLoad.current = now;
    try {
      const [attRes, bRes] = await Promise.all([attendanceAPI.today(), leaveAPI.balance()]);
      setAttendance(attRes); setLeaveBalance(bRes);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onRefresh = () => {
      lastLoad.current = 0;
      load();
    };

    window.addEventListener("hris:app-refresh", onRefresh);
    return () => window.removeEventListener("hris:app-refresh", onRefresh);
  }, [load]);

  if (loading) return <PageLoader />;

  const isCheckedIn = !!attendance?.check_in && !attendance?.check_out;
  const isCheckedOut = !!attendance?.check_out;
  const initials = (user?.name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const remainingDays = leaveBalance?.total_remaining ?? 0;
  const usedDays = (leaveBalance?.balances?.reduce((acc, curr) => acc + (curr.used_days || 0), 0)) ?? 0;
  const totalLeaveDays = remainingDays + usedDays;
  const leavePercent = totalLeaveDays > 0 ? Math.min(100, Math.round((remainingDays / totalLeaveDays) * 100)) : 0;
  const statusLabel = isCheckedOut ? "Selesai Kerja" : isCheckedIn ? "Sedang Bekerja" : "Belum Absen";
  const statusCopy = isCheckedOut ? "Absensi hari ini sudah lengkap." : isCheckedIn ? "Kamu sedang dalam jam kerja." : "Mulai absen sebelum bekerja.";
  const statusPercent = isCheckedOut ? 100 : isCheckedIn ? 55 : 0;
  const attendanceAction = isCheckedIn ? "Check Out" : isCheckedOut ? "Lihat Detail" : "Check In";
  const ringStyle = { background: `conic-gradient(#F9C74F ${statusPercent}%, rgba(255,255,255,0.22) 0)` };

  return (
    <div className="relative flex min-h-[calc(100dvh-80px)] flex-col overflow-hidden bg-[#F4F1F2] text-gray-900 antialiased">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[238px] bg-primary" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[238px] opacity-25 [background-image:radial-gradient(circle_at_18px_18px,rgba(255,255,255,0.55)_1.5px,transparent_1.8px)] [background-size:22px_22px]" />
      <div className="pointer-events-none absolute left-0 right-0 top-[214px] h-24 rounded-t-[28px] bg-[#F4F1F2]" />

      <header className="relative px-5 pt-7 pb-4 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-white/75">{getGreeting()}</p>
            <h1 className="mt-1 text-2xl font-black leading-tight truncate">Hi, {user?.name?.split(" ")?.[0] || "Karyawan"}</h1>
            <p className="mt-1 max-w-[220px] text-[11px] font-medium leading-snug text-white/70">{user?.job_title || "Employee"} - {user?.work_location?.name || user?.location || "Kantor Utama"}</p>
          </div>

          <button onClick={() => navigate("/profile")} className="h-12 w-12 rounded-xl border border-white/25 bg-white/15 p-0.5 shadow-lg backdrop-blur active:scale-95 transition">
            <div className="h-full w-full rounded-[10px] bg-white overflow-hidden flex items-center justify-center">
              {user?.image ? (
                <img src={`data:image/png;base64,${user.image}`} alt="profile" className="h-full w-full object-cover" />
              ) : (
                <span className="text-primary font-black text-sm">{initials}</span>
              )}
            </div>
          </button>
        </div>
      </header>

      <main className="relative flex flex-1 flex-col px-5 pb-7">
        <section className="relative overflow-hidden rounded-2xl border border-white/20 bg-gradient-to-br from-red-500 via-primary to-primary-dark p-5 text-white shadow-[0_18px_42px_rgba(198,40,40,0.24)]">
          <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:linear-gradient(135deg,rgba(255,255,255,0.34)_0_1px,transparent_1px_18px)]" />
          <div className="pointer-events-none absolute -right-2 -top-1 h-20 w-24 rounded-bl-2xl bg-white/10" />
          <div className="pointer-events-none absolute right-5 top-7 h-12 w-12 rounded-xl border border-white/30 bg-white/20" />
          <div className="pointer-events-none absolute right-12 top-4 h-5 w-11 rounded-md bg-white/20" />

          <div className="relative max-w-[205px]">
            <p className="text-[11px] font-black uppercase tracking-wider text-white/70">Status Absensi</p>
            <h2 className="mt-1 text-xl font-black leading-tight">{statusLabel}</h2>
            <p className="mt-1 text-[11px] font-semibold leading-snug text-white/70">{statusCopy}</p>
          </div>

          <div className="relative mt-5 flex items-end justify-between gap-4">
            <button onClick={() => navigate("/attendance")} className="rounded-lg bg-white px-3.5 py-2 text-[10px] font-black uppercase tracking-wider text-primary shadow-[0_10px_22px_rgba(90,0,0,0.16)] active:scale-[0.98] transition">
              {attendanceAction}
            </button>
            <div className="flex items-center gap-2">
              <div className="grid h-[46px] w-[46px] place-items-center rounded-full" style={ringStyle}>
                <div className="grid h-[34px] w-[34px] place-items-center rounded-full bg-primary text-[10px] font-black text-white">{statusPercent}%</div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-4 grid grid-cols-4 gap-2">
          <QuickAction label="Absensi" tone="text-primary border-red-100 bg-red-50" onClick={() => navigate("/attendance")} icon={<ClockIcon />} />
          <QuickAction label="Riwayat" tone="text-sky-600 border-sky-100 bg-sky-50" onClick={() => navigate("/attendance/history")} icon={<HistoryIcon />} />
          <QuickAction label="Cuti" tone="text-amber-600 border-amber-100 bg-amber-50" onClick={() => navigate("/leave")} icon={<CalendarIcon />} />
          <QuickAction label="Profil" tone="text-rose-600 border-rose-100 bg-rose-50" onClick={() => navigate("/profile")} icon={<UserIcon />} />
        </section>

        <LeaveBalanceCard
          remainingDays={remainingDays}
          usedDays={usedDays}
          totalLeaveDays={totalLeaveDays}
          leavePercent={leavePercent}
          onClick={() => navigate("/leave")}
        />

        <WorkLocationCard workLocation={user?.work_location} />

        <AppFooter />
      </main>
    </div>
  );
}

function AppFooter() {
  return (
    <footer className="mt-auto pt-5 text-center">
      <div className="mx-auto inline-flex items-center gap-2 rounded-xl border border-white bg-white/65 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-gray-400 shadow-[0_10px_24px_rgba(90,20,20,0.06)]">
        <span>HRIS Mobile v2.1.2</span>
        <span className="h-1 w-1 rounded-full bg-gray-300" />
        <span>(c) 2026 PJS</span>
      </div>
    </footer>
  );
}

function QuickAction({ label, icon, onClick, tone }) {
  return (
    <button onClick={onClick} className="min-w-0 rounded-2xl border border-white bg-white p-2.5 shadow-[0_12px_28px_rgba(90,20,20,0.08)] active:scale-95 transition">
      <span className={`mx-auto flex h-10 w-10 items-center justify-center rounded-xl border ${tone}`}>
        {icon}
      </span>
      <span className="mt-1.5 block truncate text-center text-[9px] font-black leading-tight text-gray-600">{label}</span>
    </button>
  );
}

function LeaveBalanceCard({ remainingDays, usedDays, totalLeaveDays, leavePercent, onClick }) {
  return (
    <button onClick={onClick} className="mt-4 w-full overflow-hidden rounded-2xl border border-white bg-white text-left shadow-[0_14px_32px_rgba(90,20,20,0.10)] active:scale-[0.99] transition">
      <div className="relative p-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-red-50 to-transparent" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-wider text-primary/70">Leave Balance</p>
            <h3 className="mt-1 text-lg font-black leading-tight text-gray-900">Saldo Cuti</h3>
            <p className="mt-1 text-[11px] font-bold text-gray-400">Ringkasan jatah cuti kamu saat ini</p>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-100 bg-amber-50 text-amber-600">
            <CalendarIcon />
          </span>
        </div>

        <div className="relative mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-red-100 bg-red-50/70 p-3">
            <p className="text-[10px] font-black uppercase tracking-wider text-primary/70">Tersedia</p>
            <p className="mt-1 text-3xl font-black leading-none text-primary">{remainingDays}</p>
            <p className="mt-1 text-[10px] font-bold text-gray-400">Hari</p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
            <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Terpakai</p>
            <p className="mt-1 text-3xl font-black leading-none text-gray-800">{usedDays}</p>
            <p className="mt-1 text-[10px] font-bold text-gray-400">Hari</p>
          </div>
        </div>

        <div className="relative mt-4">
          <div className="mb-2 flex items-center justify-between text-[10px] font-black uppercase tracking-wider">
            <span className="text-gray-400">Total {totalLeaveDays} Hari</span>
            <span className="text-primary">{leavePercent}% Sisa</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${leavePercent}%` }} />
          </div>
        </div>
      </div>
    </button>
  );
}

function WorkLocationCard({ workLocation }) {
  const active = isGeofenceActive(workLocation);
  const lat = workLocation?.latitude;
  const lng = workLocation?.longitude;
  const mapsUrl = active ? `https://www.google.com/maps?q=${lat},${lng}` : null;

  return (
    <div className="mt-4 w-full overflow-hidden rounded-2xl border border-white bg-white shadow-[0_14px_32px_rgba(90,20,20,0.10)]">
      <div className="relative p-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-sky-50 to-transparent" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-wider text-primary/70">Work Location</p>
            <h3 className="mt-1 truncate text-lg font-black leading-tight text-gray-900">{workLocation?.name || "Lokasi Kerja"}</h3>
            <p className="mt-1 text-[11px] font-bold text-gray-400">
              {active ? `Geofence aktif · radius ${Math.round(Number(workLocation.radius))} m` : "Tanpa batas radius — bebas absen di mana saja"}
            </p>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-600">
            <PinIcon />
          </span>
        </div>

        {active ? (
          <div className="relative mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Latitude</p>
              <p className="mt-1 text-sm font-black text-gray-800">{Number(lat).toFixed(6)}</p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Longitude</p>
              <p className="mt-1 text-sm font-black text-gray-800">{Number(lng).toFixed(6)}</p>
            </div>
          </div>
        ) : (
          <div className="relative mt-4 rounded-xl border border-sky-100 bg-sky-50/70 p-3">
            <p className="text-[11px] font-bold text-sky-700">
              Admin belum mengatur titik &amp; radius lokasi kerja kamu di Odoo — absen bisa dilakukan dari mana saja.
            </p>
          </div>
        )}

        {active && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="relative mt-3 inline-flex items-center gap-1.5 text-[11px] font-black text-primary"
          >
            Buka di Google Maps →
          </a>
        )}
      </div>
    </div>
  );
}

function PinIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v5h5" />
      <path d="M3.1 13a9 9 0 1 0 2.3-7.1L3 8" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}